import React, { useCallback } from 'react';
import {
  V1AllergiesCard,
  V1DiagnosesCard,
  V1MedicationsCard,
} from '@features/medications-workspace';
import { useSubjectLanguageVariants } from '@features/user';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

export type UnifiedClinicalModule = 'medications' | 'diagnoses' | 'allergies';

const MODULE_TITLE: Record<UnifiedClinicalModule, string> = {
  medications: 'Medications',
  diagnoses: 'Diagnoses',
  allergies: 'Allergies',
};

interface UnifiedClinicalEditModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  module: UnifiedClinicalModule;
  /** Source identifier for analytics */
  source: string;
}

/**
 * The context-bar edit modal behind feature_unified_medications_card: hosts
 * the same V1 clinical-card container the patient page renders, so the bar
 * and the chart share one component per module (full CRUD, unified statuses,
 * Manage-in-DoseSpot, consent section). The containers own the fine-grained
 * CRUD analytics (unifiedMedications / clinicalCards families);
 * `trackImpression={false}` because this surface is already counted by the
 * host's editModalOpened, and this dialog closes the pair with
 * editModalClosed — same contract as the legacy edit modals.
 */
export const UnifiedClinicalEditModal: React.FC<UnifiedClinicalEditModalProps> = ({
  open,
  onClose,
  patientId,
  module,
  source,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { singularCap } = useSubjectLanguageVariants();
  const { patientContextBar: contextBarTracking } = useEventTracking();

  const handleClose = useCallback(() => {
    contextBarTracking.editModalClosed({
      patient_id: patientId,
      data_type: module,
      had_changes: false,
      source,
    });
    onClose();
  }, [contextBarTracking, module, onClose, patientId, source]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      slotProps={{
        paper: {
          sx: {
            height: fullScreen ? '100%' : '80vh',
            maxHeight: fullScreen ? '100%' : '80vh',
            bgcolor: 'background.paper',
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider',
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {`Edit ${singularCap} ${MODULE_TITLE[module]}`}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2 }}>
          {module === 'medications' && (
            <V1MedicationsCard patientId={patientId} trackImpression={false} />
          )}
          {module === 'diagnoses' && (
            <V1DiagnosesCard patientId={patientId} trackImpression={false} />
          )}
          {module === 'allergies' && (
            <V1AllergiesCard patientId={patientId} trackImpression={false} />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
